import React from 'react';
import { useCallback, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Button } from '../../components/ui/Button/Button';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import {
    useGetSupportChatQuery,
    useGetSupportRequestQuery,
    useSendSupportAnswerMutation,
} from '../../entities/support/api/supportApi';
import type { TicketMessage, TicketStatus } from '../../entities/support/model/types';
import { cx } from '../../utils/cx';
import styles from './SupportAnswerDetailPage.module.scss';
import { statusLabels } from '../../entities/support/model/constants';
import { BackButton } from '../../components/ui/BackButton/BackButton';

interface FactProps {
    label: string;
    children: ReactNode;
}

const Fact: FC<FactProps> = ({ label, children }) => (
    <div className={styles.fact}>
        <span className={styles.factLabel}>{label}</span>
        <div className={styles.factValue}>{children}</div>
    </div>
);

interface MessageProps {
    text: string;
    date: string;
    author: string;
    isSupport?: boolean;
}

const Message: FC<MessageProps> = ({ text, date, author, isSupport = false }) => (
    <article className={cx(styles.message, isSupport && styles.messageSupport)}>
        <header className={styles.messageHead}>
            <b className={styles.messageAuthor}>{author}</b>
            <time className={styles.messageDate}>{date}</time>
        </header>
        <p className={styles.messageText}>{text}</p>
    </article>
);

export function SupportAnswerDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [isAnswering, setIsAnswering] = useState(false);
    const [answer, setAnswer] = useState('');

    const {
        data: ticket,
        isLoading,
        isError,
    } = useGetSupportRequestQuery(id ?? '', { skip: !id });

    const { data: chat = [] } = useGetSupportChatQuery(id ?? '', { skip: !id });

    const [sendAnswer, { isLoading: isSending }] = useSendSupportAnswerMutation();

    const closeAnswerForm = useCallback(() => {
        setIsAnswering(false);
        setAnswer('');
    }, []);

    const handleSend = useCallback(async () => {
        const text = answer.trim();
        if (!id || !text) return;

        try {
            await sendAnswer({ requestId: id, text }).unwrap();
            closeAnswerForm();
        } catch {
            /* тост об ошибке уже показал notifyOnError */
        }
    }, [answer, id, sendAnswer, closeAnswerForm]);

    if (isLoading) {
        return (
            <div className={styles.scroll}>
                <p className={styles.placeholder}>Загружаем обращение…</p>
            </div>
        );
    }

    if (isError || !ticket) {
        return (
            <div className={styles.scroll}>
                <div className={styles.placeholderBox}>
                    <p className={styles.placeholder}>
                        Обращение не найдено. Возможно, оно удалено или ссылка устарела.
                    </p>
                    <Button onClick={() => navigate('/support-answers')}>
                        Ко всем обращениям
                    </Button>
                </div>
            </div>
        );
    }

    const isClosed = ticket.status === 'done';
    const authorName = ticket.userName || ticket.name || 'Вы';

    return (
        <div className={styles.scroll}>
            <div className={styles.head}>
                <BackButton />
                <PageHeader
                    title={ticket.problem || 'Запросы в техническую поддержку'}
                    meta={[`Создано ${ticket.createdAt}`, `Срок ответа — ${ticket.decisionDate}`]}
                    actions={
                        <Button onClick={() => navigate('/support-answers')}>
                            Ко всем обращениям
                        </Button>
                    }
                />
            </div>

            <section className={styles.card}>
                <header className={styles.cardHead}>
                    <h2 className={styles.cardTitle}>Заявка</h2>
                    <span className={cx(styles.status, styles[`status_${ticket.status}`])}>
                        {statusLabels[ticket.status]}
                    </span>
                </header>

                <div className={styles.facts}>
                    {ticket.projectId && ticket.projectName && (
                        <Fact label="Проект">
                            <Link className={styles.link} to={`/projects/${ticket.projectId}`}>
                                {ticket.projectName}
                            </Link>
                        </Fact>
                    )}

                    <Fact label="Автор">{authorName}</Fact>
                    <Fact label="Организация">{ticket.organization || '—'}</Fact>
                    <Fact label="Почта для ответа">
                        <a className={styles.link} href={`mailto:${ticket.email}`}>
                            {ticket.email}
                        </a>
                    </Fact>
                    <Fact label="Дата создания">{ticket.createdAt}</Fact>
                    <Fact label="Срок ответа">{ticket.decisionDate}</Fact>

                    {ticket.comment && (
                        <Fact label="Комментарий">
                            <span className={styles.factText}>{ticket.comment}</span>
                        </Fact>
                    )}

                    {ticket.attachments.length > 0 && (
                        <Fact label="Вложения">
                            <ul className={styles.files}>
                                {ticket.attachments.map((file) => (
                                    <li key={file.name}>
                                        <a
                                            className={styles.link}
                                            href={file.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {file.name}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </Fact>
                    )}
                </div>

                <div className={styles.thread}>
                    <h3 className={styles.threadTitle}>Переписка</h3>

                    <Message
                        text={ticket.description}
                        date={ticket.createdAt}
                        author={authorName}
                    />

                    {chat.map((message: TicketMessage) => (
                        <Message
                            key={message.id}
                            text={message.text}
                            date={message.createdAt}
                            author={message.isSupport ? 'Техподдержка' : authorName}
                            isSupport={message.isSupport}
                        />
                    ))}

                    {chat.length === 0 && !isClosed && (
                        <p className={styles.threadEmpty}>
                            Ответа пока нет. Инженер напишет сюда, когда возьмёт заявку в работу.
                        </p>
                    )}

                    {isClosed ? (
                        <p className={styles.threadClosed}>
                            Заявка закрыта. Если вопрос остался — создайте новое обращение.
                        </p>
                    ) : isAnswering ? (
                        <div className={styles.answerForm}>
                            <label className={styles.answerLabel} htmlFor="answer">
                                Ваше сообщение
                            </label>
                            <textarea
                                id="answer"
                                className={styles.answerField}
                                value={answer}
                                rows={4}
                                maxLength={2000}
                                placeholder="Опишите, что изменилось, или уточните детали"
                                autoFocus
                                onChange={(event) => setAnswer(event.target.value)}
                            />
                            <div className={styles.answerActions}>
                                <Button
                                    variant="primary"
                                    disabled={!answer.trim() || isSending}
                                    onClick={handleSend}
                                >
                                    {isSending ? 'Отправляем...' : 'Отправить'}
                                </Button>
                                <button
                                    type="button"
                                    className={styles.linkBtn}
                                    onClick={closeAnswerForm}
                                >
                                    Отмена
                                </button>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className={styles.answerOpen}
                            onClick={() => setIsAnswering(true)}
                        >
                            Написать в поддержку
                        </button>
                    )}
                </div>
            </section>
        </div>
    );
}